import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const tl = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v || 0);
const dt = (v) => (v ? new Date(v).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-');
const products = (s) => (s.items?.length ? s.items.map(i => `${i.product_name} (${i.quantity})`).join(', ') : `${s.product_name || '-'} (${s.quantity || 0})`);

export function DeletedSalesDialog({ onRestored }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const token = () => localStorage.getItem('token');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/sales/deleted?token=${token()}`);
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silme geçmişi yüklenemedi');
    }
  };

  const restore = async (r) => {
    if (!window.confirm(`"${r.sale.customer_name}" satışı geri alınsın mı?`)) return;
    setBusy(r.id);
    try {
      const { data } = await axios.post(`${API_BASE}/api/sales/deleted/${r.id}/restore?token=${token()}`);
      toast.success(data.message);
      await load();
      onRestored?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Geri alma başarısız');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => { setOpen(true); load(); }} className="text-sm" data-testid="deleted-sales-btn">
        <History className="w-4 h-4 mr-2" />Silme Geçmişi
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="deleted-sales-dialog">
          <DialogHeader><DialogTitle>Silinen Satışlar</DialogTitle></DialogHeader>
          {rows.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center" data-testid="deleted-sales-empty">Silinmiş satış bulunmuyor.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-3">Silinme</th><th className="py-2 pr-3">Silen</th><th className="py-2 pr-3">Satış Tarihi</th>
                    <th className="py-2 pr-3">Müşteri</th><th className="py-2 pr-3">Temsilci</th><th className="py-2 pr-3">Ürünler</th>
                    <th className="py-2 pr-3 text-right">Tutar</th><th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className="border-b last:border-0 align-top" data-testid={`deleted-sale-row-${r.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{dt(r.deleted_at)}</td>
                      <td className="py-2 pr-3 whitespace-nowrap font-medium">{r.deleted_by_name || r.deleted_by}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{dt(r.sale.sale_date)}</td>
                      <td className="py-2 pr-3">{r.sale.customer_name}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{r.sale.representative}</td>
                      <td className="py-2 pr-3 text-xs text-gray-600 max-w-[220px]">{products(r.sale)}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">{tl(r.sale.price)}</td>
                      <td className="py-2">
                        <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => restore(r)} data-testid={`restore-sale-${r.id}`}>
                          <RotateCcw className="w-3.5 h-3.5 mr-1" />Geri Al
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
